import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IonActionSheet, IonCard, IonCardContent, IonIcon, IonSkeletonText } from '@ionic/react';
import {
  chevronForwardOutline,
  leafOutline,
  thermometerOutline,
  waterOutline,
  compassOutline,
  rainyOutline,
  flowerOutline,
  gridOutline,
  speedometerOutline,
} from 'ionicons/icons';
import type { Device } from '../../../domain/Device';
import { hasComponent } from '../../../domain/Component';
import { LOCATION_SOURCE_LABEL } from '../../../domain/DeviceLocation';
import { formatGdd, lotNow, soilProbesOf, stageTitle, stationsForMetric, type Lot, type LotNowMetric } from '../../../domain/Lot';
import { formatRelative, round1, shortDate, soilMoistureLabel, stationInsights } from '../../../domain/WeatherStation';
import { useLotForecast } from '../../../application/use-cases/lots/useLotForecast';
import StationInsights from '../station/StationInsights';
import { ForecastDays } from '../station/StationForecast';
import HomeHero, { type HeroExtras } from '../home/HomeHero';
import { ConditionCard, MetricTiles, RecommendCard, SoilCard, type MetricTileItem } from '../home/HomeCards';
import { useSkyMood } from '../home/useSkyMood';
import { fieldCondition } from '../../../domain/FieldCondition';
import '../station/station.css';
import '../../pages/Lots.css';

interface Props {
  lot: Lot;
  // Las estaciones del lote que ve el usuario (con sus umbrales y ubicación).
  devices: Device[];
  hero?: HeroExtras;
}

// El home de un lote: en qué etapa está, cómo está ahora (promedio de sus
// estaciones), la lluvia de cada pluviómetro, qué vigilar y el pronóstico.
// Cada estación se abre con su home de siempre.
const LotHome: React.FC<Props> = ({ lot, devices, hero }) => {
  const history = useHistory();
  const status = lot.status;
  const ph = status?.phenology;
  const now = lotNow(lot.stations);
  // Tocar una métrica abre su detalle en una estación del lote: directo si
  // la mide una sola, si no se elige cuál.
  const [sheet, setSheet] = useState<{ header: string; options: { text: string; href: string }[] } | null>(null);
  const choose = (header: string, options: { text: string; href: string }[]) => {
    if (options.length === 1) history.push(options[0].href);
    else if (options.length > 1) setSheet({ header, options });
  };
  const METRIC_TITLE: Record<LotNowMetric, string> = {
    temperatura: 'Temperatura',
    humedad: 'Humedad',
    viento: 'Viento',
    lluvia: 'Lluvia',
    presion: 'Presión',
  };
  const openMetric = (metric: LotNowMetric) =>
    choose(
      `${METRIC_TITLE[metric]}: ¿de qué estación?`,
      stationsForMetric(lot.stations, metric).map((o) => ({ text: o.label, href: `/app/stations/${o.id}/${metric}` }))
    );
  const openSoil = () =>
    choose(
      'Humedad de suelo: ¿qué sonda?',
      soilProbesOf(lot.stations).map((p) => ({ text: p.label, href: `/app/stations/${p.id}/suelo/${p.channel}` }))
    );
  const soilThresholds = (devices.find((d) => hasComponent(d.components, 'soil')) ?? devices[0])?.thresholds.soil;
  const { forecast, status: forecastStatus, location } = useLotForecast(devices);
  const airDevice = devices.find((d) => hasComponent(d.components, 'air'));
  const rainStations = lot.stations.filter((s) => s.now.rainLast7d !== null);
  const maxRain = Math.max(1, ...rainStations.map((s) => s.now.rainLast7d ?? 0));
  const insights =
    airDevice && now.temperature !== null
      ? stationInsights(now.temperature, now.humidity, now.windSpeed, 0, null, airDevice.thresholds, forecast?.hourly)
      : [];

  // El suelo cuenta igual que en una estación: un lote con la humedad de
  // suelo fuera del rango ideal no puede quedar "Óptima".
  const soilState = now.soil !== null && soilThresholds ? soilMoistureLabel(now.soil, soilThresholds) : null;
  const condition = fieldCondition(insights, soilState);
  const spray = insights.find((i) => i.kind === 'spray');
  const online = lot.stations.some((s) => s.status === 'online');
  const mood = useSkyMood({
    temperature: now.temperature,
    windKmh: now.windSpeed,
    rainLastHour: null,
    weatherCode: forecast?.hourly[0]?.weatherCode ?? null,
  });
  const coldest = status?.last7.coldest;
  const hottest = status?.last7.hottest;
  const has = (metric: LotNowMetric) => stationsForMetric(lot.stations, metric).length > 0;
  const tiles: MetricTileItem[] = [
    {
      key: 'temp',
      tone: 'temp' as const,
      icon: thermometerOutline,
      value: now.temperature !== null ? `${round1(now.temperature)}°` : '—',
      label: 'Temp.',
      sub: coldest != null && hottest != null ? `7d ${Math.round(coldest)}° a ${Math.round(hottest)}°` : undefined,
      onClick: has('temperatura') ? () => openMetric('temperatura') : undefined,
    },
    {
      key: 'rh',
      tone: 'humidity' as const,
      icon: waterOutline,
      value: now.humidity !== null ? `${Math.round(now.humidity)}%` : '—',
      label: 'Humedad',
      onClick: has('humedad') ? () => openMetric('humedad') : undefined,
    },
    {
      key: 'wind',
      tone: 'wind' as const,
      icon: compassOutline,
      value: now.windSpeed !== null ? `${Math.round(now.windSpeed)}` : '—',
      unit: 'km/h',
      label: 'Viento',
      onClick: has('viento') ? () => openMetric('viento') : undefined,
    },
    {
      key: 'rain',
      tone: 'rain' as const,
      icon: rainyOutline,
      value: now.rainToday !== null ? `${round1(now.rainToday)}` : '—',
      unit: 'mm',
      label: 'Lluvia',
      sub: now.rainLast7d !== null ? `7d ${Math.round(now.rainLast7d)} mm` : undefined,
      onClick: has('lluvia') ? () => openMetric('lluvia') : undefined,
    },
    ...(now.pressure !== null
      ? [
          {
            key: 'pressure',
            tone: 'pressure' as const,
            icon: speedometerOutline,
            value: `${Math.round(now.pressure)}`,
            unit: 'hPa',
            label: 'Presión',
            onClick: () => openMetric('presion'),
          },
        ]
      : []),
  ];
  const meta = [
    ...(lot.crop ? [{ icon: leafOutline, text: lot.crop }] : []),
    ...(ph && lot.stations.length > 0 ? [{ icon: flowerOutline, text: lot.stages[ph.stageIndex].name }] : []),
    ...(lot.areaHa !== null
      ? [
          {
            icon: gridOutline,
            text: `${lot.areaHa.toLocaleString('es-AR')} ha`,
          },
        ]
      : []),
  ];

  return (
    <section className="lot-home">
      <HomeHero
        mood={mood}
        title={lot.name}
        status={lot.stations.length > 0 ? { label: online ? 'Activo' : 'Sin conexión', online } : undefined}
        meta={meta}
        switcher={hero?.switcher}
        alertCount={hero?.alertCount ?? 0}
        onBack={hero?.onBack}
      >
        {condition && (
          <ConditionCard label="Condición del lote" condition={condition} onClick={() => history.push(`/app/lots/${lot.id}`)} />
        )}
      </HomeHero>

      <div className="home-body">
        {lot.stations.length > 0 && (
          <p className="home-source">
            {now.sources.length > 0
              ? `Promedio de ${now.sources.length === 1 ? '1 estación' : `${now.sources.length} estaciones`} · ${now.sources.join(', ')}${now.latestAt ? ` · ${formatRelative(now.latestAt)}` : ''}`
              : 'Sin datos de las últimas 2 horas'}
          </p>
        )}
        {now.soil !== null && soilThresholds && (
          <SoilCard
            title="Humedad del suelo"
            value={now.soil}
            lowPct={soilThresholds.lowPct}
            saturatedPct={soilThresholds.saturatedPct}
            state={soilState?.label ?? soilMoistureLabel(now.soil, soilThresholds).label}
            onClick={openSoil}
          />
        )}
        {lot.stations.length > 0 && <MetricTiles items={tiles} />}
        {spray && <RecommendCard title={spray.title} detail={spray.detail} onClick={() => history.push('/app/ai')} />}
        <IonActionSheet
          isOpen={sheet !== null}
          onDidDismiss={() => setSheet(null)}
          header={sheet?.header}
          buttons={[
            ...(sheet?.options ?? []).map((o) => ({ text: o.text, handler: () => history.push(o.href) })),
            { text: 'Cancelar', role: 'cancel' },
          ]}
        />

        <IonCard button onClick={() => history.push(`/app/lots/${lot.id}`)}>
          <IonCardContent>
            <div className="station-section-head">
              <span>Etapa del cultivo</span>
              {lot.stations.length > 0 && now.latestAt && <span className="station-source">{formatRelative(now.latestAt)}</span>}
            </div>
            {!lot.cycleStart && <p className="lot-card-note">Sin fecha de siembra: cargala para seguir el cultivo.</p>}
            {lot.cycleStart && lot.stations.length === 0 && (
              <p className="lot-card-note">Sumá una estación al lote para seguir el cultivo.</p>
            )}
            {ph && lot.stations.length > 0 && (
              <>
                <span className="lot-card-stage">
                  <span className="lot-card-stage-name">{stageTitle(lot.stages[ph.stageIndex])}</span>
                  <span className="lot-card-gdd">
                    {formatGdd(ph.accumulated)} °Cd · {ph.progressPct}%
                  </span>
                </span>
                <span className="lot-progress big">
                  <span style={{ width: `${ph.progressPct}%` }} />
                </span>
                <span className="lot-card-sub">
                  {ph.nextIndex !== null
                    ? `${lot.stages[ph.nextIndex].name}: faltan ${formatGdd(ph.remaining ?? 0)} °Cd${ph.nextDate ? ` · ~${shortDate(ph.nextDate)}` : ''}`
                    : 'Llegó a la última etapa del ciclo'}
                </span>
              </>
            )}
          </IonCardContent>
        </IonCard>
      </div>

      {insights.length > 0 && (
        <StationInsights items={insights} settingsHref={airDevice ? `/app/devices/${airDevice.id}/thresholds` : undefined} />
      )}

      {status && ph && (
        <IonCard>
          <IonCardContent>
            <div className="station-section-head">
              <span>Qué vigilar</span>
              <span className="station-source">{lot.stages[ph.stageIndex].name}</span>
            </div>
            <p className="lot-home-watch">{lot.stages[ph.stageIndex].watch ?? 'Sin indicaciones cargadas para esta etapa.'}</p>
            <div className="lot-last7">
              <div className={status.last7.coldest !== null && status.last7.coldest <= 0 ? 'is-bad' : ''}>
                <span>Mínima 7 d</span>
                <strong>{status.last7.coldest !== null ? `${round1(status.last7.coldest)}°` : '—'}</strong>
              </div>
              <div className={status.last7.hottest !== null && status.last7.hottest >= 30 ? 'is-caution' : ''}>
                <span>Máxima 7 d</span>
                <strong>{status.last7.hottest !== null ? `${round1(status.last7.hottest)}°` : '—'}</strong>
              </div>
              <div>
                <span>Hoja mojada</span>
                <strong>{status.last7.wetHours !== null ? `${status.last7.wetHours} h` : '—'}</strong>
              </div>
              <div>
                <span>Lluvia 7 d</span>
                <strong>{status.last7.rainMm !== null ? `${round1(status.last7.rainMm)} mm` : '—'}</strong>
              </div>
            </div>
          </IonCardContent>
        </IonCard>
      )}

      {rainStations.length > 0 && (
        <IonCard>
          <IonCardContent>
            <div className="station-section-head">
              <span>Lluvia sobre el lote · 7 días</span>
              <span className="station-source">{now.rainLast7d !== null ? `promedio ${round1(now.rainLast7d)} mm` : ''}</span>
            </div>
            <ul className="lot-rain-stations">
              {rainStations.map((s) => (
                <li key={s.id}>
                  <span>{s.name}</span>
                  <span className="station-agro-month-bar">
                    <span
                      style={{
                        width: `${((s.now.rainLast7d ?? 0) / maxRain) * 100}%`,
                      }}
                    />
                  </span>
                  <strong>{round1(s.now.rainLast7d ?? 0)} mm</strong>
                </li>
              ))}
            </ul>
          </IonCardContent>
        </IonCard>
      )}

      {lot.stations.length > 0 && (
        <IonCard>
          <IonCardContent>
            <div className="station-section-head">
              <span>Estaciones</span>
            </div>
            <ul className="lot-home-stations">
              {lot.stations.map((s) => (
                <li key={s.id}>
                  <button onClick={() => history.push(`/app/stations/${s.id}`)}>
                    <span className={`lot-home-dot ${s.status}`} aria-label={s.status === 'online' ? 'En línea' : 'Sin conexión'} />
                    <span className="lot-home-station-name">{s.name}</span>
                    <span className="lot-home-station-values">
                      {[
                        s.now.temperature ? `${round1(s.now.temperature.value)}°` : null,
                        s.now.rainToday !== null ? `${round1(s.now.rainToday)} mm` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </span>
                    <IonIcon icon={chevronForwardOutline} />
                  </button>
                </li>
              ))}
            </ul>
          </IonCardContent>
        </IonCard>
      )}

      {lot.stations.length > 0 && (
        <IonCard className="station-forecast-card">
          <IonCardContent>
            <div className="station-section-head">
              <span>Pronóstico 7 días</span>
              <span className="station-source">
                {location ? `${LOCATION_SOURCE_LABEL[location.source]} · ` : ''}
                vía Open-Meteo
              </span>
            </div>
            {forecastStatus === 'loading' && (
              <IonSkeletonText
                animated
                style={{
                  width: '100%',
                  height: 220,
                  borderRadius: 10,
                  margin: 0,
                }}
              />
            )}
            {forecastStatus === 'ok' && forecast && <ForecastDays days={forecast.daily} />}
            {forecastStatus === 'error' && <p className="station-empty">No se pudo obtener el pronóstico.</p>}
            {forecastStatus === 'unset' && <p className="station-empty">Ninguna estación del lote tiene ubicación todavía.</p>}
          </IonCardContent>
        </IonCard>
      )}
    </section>
  );
};

export default LotHome;
